// El cuadro de eliminación. La idea que ordena todo (PLAN.md):
//
//   EL CUADRO NO GUARDA PAREJAS, GUARDA ORÍGENES.
//
// Cada lugar de la primera ronda dice de dónde sale: "1º del grupo A", una
// pareja fija (solo eliminación) o "libre" (la de enfrente pasa directo). De la
// segunda ronda en adelante no hace falta guardar nada: el partido i de una
// ronda lo juegan los ganadores de los partidos 2i y 2i+1 de la anterior.
//
// Quién ocupa cada lugar se CALCULA acá cada vez (resolverCuadro). Así, si se
// corrige un resultado de grupo, el cuadro se reacomoda solo. Lo único que se
// guarda de un partido de cuadro, además del marcador, es quiénes lo jugaron:
// ese es un hecho, y permite avisar si el cuadro después dice otra cosa.

import { ganador, type Marcador } from './marcador';

// ── Los lugares de la primera ronda ───────────────────────────────────────

export type Lugar =
  | { tipo: 'grupo'; grupo: number; puesto: number } // grupo = id del grupo; puesto 1 = el primero
  | { tipo: 'pareja'; pareja: number }
  | { tipo: 'libre' };

/** La potencia de 2 en la que entran `n`: 5 -> 8, 8 -> 8, 9 -> 16. */
export function tamanioDeCuadro(n: number): number {
  let t = 2;
  while (t < n) t *= 2;
  return t;
}

/**
 * Qué sembrado va en cada posición de la primera ronda. Para 8:
 * [1,8,4,5,2,7,3,6] -> 1 contra 8, 4 contra 5 | 2 contra 7, 3 contra 6.
 * El 1 y el 2 solo se cruzan en la final; el 1 juega con el último.
 */
export function ordenDeSiembra(tamanio: number): number[] {
  let orden = [1, 2];
  while (orden.length < tamanio) {
    const total = orden.length * 2 + 1;
    orden = orden.flatMap((s) => [s, total - s]);
  }
  return orden;
}

/**
 * El cuadro que sale de los grupos. `grupos` son los ids en orden (A, B, C...).
 *
 * Los primeros van sembrados en ese orden (el 1º del A es el sembrado 1: en el
 * A cayó la cabeza de serie 1). Los segundos y terceros se acomodan en los
 * lugares que quedan, buscando que dos del mismo grupo no se crucen en la
 * primera ronda y, si se puede, queden en mitades distintas: con 4 grupos da
 * los cruces de siempre (1ºA-2ºB, 1ºB-2ºA, 1ºC-2ºD, 1ºD-2ºC).
 *
 * Si los clasificados no llenan el cuadro, los mejores sembrados pasan directo.
 */
export function sembrarDesdeGrupos(grupos: readonly number[], pasan: number): Lugar[] {
  const k = grupos.length;
  const tamanio = tamanioDeCuadro(k * pasan);
  const orden = ordenDeSiembra(tamanio);
  const posDe = new Map(orden.map((s, pos) => [s, pos]));
  const lugares: Lugar[] = Array.from({ length: tamanio }, () => ({ tipo: 'libre' }));
  const mitad = (pos: number) => (pos < tamanio / 2 ? 0 : 1);
  const partido = (pos: number) => Math.floor(pos / 2);
  /** Dónde quedó cada pareja de cada grupo: grupo -> posiciones ya ocupadas. */
  const ocupadas = new Map<number, number[]>(grupos.map((g) => [g, []]));

  const poner = (grupo: number, puesto: number, sembrado: number) => {
    const pos = posDe.get(sembrado)!;
    lugares[pos] = { tipo: 'grupo', grupo, puesto };
    ocupadas.get(grupo)!.push(pos);
  };

  grupos.forEach((g, i) => poner(g, 1, i + 1));

  for (let puesto = 2; puesto <= pasan; puesto++) {
    const faltan = [...grupos].reverse(); // de atrás para adelante: el 2º del último contra los mejores primeros
    for (let i = 0; i < k; i++) {
      const pos = posDe.get((puesto - 1) * k + i + 1)!;
      const otroPartido = (g: number) => ocupadas.get(g)!.every((p) => partido(p) !== partido(pos));
      const otraMitad = (g: number) => ocupadas.get(g)!.every((p) => mitad(p) !== mitad(pos));
      const elegido =
        faltan.find((g) => otroPartido(g) && otraMitad(g)) ?? faltan.find(otroPartido) ?? faltan[0]!;
      faltan.splice(faltan.indexOf(elegido), 1);
      poner(elegido, puesto, (puesto - 1) * k + i + 1);
    }
  }
  return lugares;
}

/**
 * El cuadro de una categoría de solo eliminación. `siembra` son los ids de
 * pareja ya ordenados: primero las cabezas de serie, después el resto como
 * salió del sorteo. Las mejores sembradas pasan directo si el cuadro no cierra.
 */
export function sembrarEliminacion(siembra: readonly number[]): Lugar[] {
  const tamanio = tamanioDeCuadro(siembra.length);
  return ordenDeSiembra(tamanio).map((s) => (s <= siembra.length ? { tipo: 'pareja', pareja: siembra[s - 1]! } : { tipo: 'libre' }));
}

/** El orden de siembra para solo eliminación: cabezas de serie y el resto sorteado. */
export function sortearSiembra(
  parejas: readonly { id: number; cabeza_serie: number | null }[],
  azar: () => number = Math.random,
): number[] {
  const cabezas = parejas.filter((p) => p.cabeza_serie !== null).sort((a, b) => a.cabeza_serie! - b.cabeza_serie!);
  const resto = parejas.filter((p) => p.cabeza_serie === null).map((p) => p.id);
  for (let i = resto.length - 1; i > 0; i--) {
    const j = Math.floor(azar() * (i + 1));
    [resto[i], resto[j]] = [resto[j]!, resto[i]!];
  }
  return [...cabezas.map((p) => p.id), ...resto];
}

// ── Rondas ────────────────────────────────────────────────────────────────

/** Cuántas rondas tiene un cuadro de ese tamaño: 8 -> 3 (cuartos, semis, final). */
export function cantidadDeRondas(tamanio: number): number {
  return Math.log2(tamanio);
}

/** El nombre de una ronda por cuántos partidos tiene. */
export function nombreDeRonda(partidos: number, corto = false): string {
  if (partidos === 1) return 'Final';
  if (partidos === 2) return corto ? 'Semi' : 'Semifinales';
  if (partidos === 4) return 'Cuartos';
  if (partidos === 8) return 'Octavos';
  return `${partidos * 2}avos`; // 16avos, 32avos
}

// ── Resolver: quién está en cada lugar ────────────────────────────────────

/** Un partido de cuadro como está guardado. `ronda` 1 = la primera; `pos` desde 0. */
export interface PartidoDeCuadro {
  id: number;
  ronda: number;
  pos: number;
  marcador: Marcador | null;
  /** Quiénes lo jugaron de verdad. Solo con resultado cargado. */
  pareja_a: number | null;
  pareja_b: number | null;
}

/** Lo que hay de un lado de un partido. */
export type Lado =
  | { pareja: number }
  | { libre: true }
  /** Todavía no se sabe: de dónde va a salir. */
  | { espera: { tipo: 'grupo'; grupo: number; puesto: number } | { tipo: 'ganador'; ronda: number; pos: number } };

export interface CruceResuelto {
  id: number;
  ronda: number;
  pos: number;
  a: Lado;
  b: Lado;
  /** El marcador que cuenta: el cargado, o un W.O. si una de las dos abandonó. Relativo a `a` y `b`. */
  marcador: Marcador | null;
  /** Quién sigue: la pareja ganadora, o la que pasa directo. */
  avanza: Lado;
  /** No se juega: una de las dos pasa directo. */
  pasaDirecto: boolean;
  porAbandono: boolean;
  /** Se puede cargar un resultado: las dos parejas se saben y ninguna abandonó. */
  sePuedeCargar: boolean;
  /**
   * El partido se jugó entre dos parejas y el cuadro ahora dice que acá van
   * otras (se corrigió algo de antes). No se pisa: se avisa al organizador.
   */
  conflicto: boolean;
}

export interface CuadroResuelto {
  rondas: CruceResuelto[][];
  /** La pareja que ganó la final, si ya se jugó. */
  campeon: number | null;
}

const esPareja = (l: Lado): l is { pareja: number } => 'pareja' in l;
const esLibre = (l: Lado): l is { libre: true } => 'libre' in l;

/**
 * `clasificados`: por grupo, sus clasificados en orden (vacío si el grupo no
 * terminó o falta un sorteo). `bajas`: parejas que abandonaron.
 */
export function resolverCuadro(
  lugares: readonly Lugar[],
  partidos: readonly PartidoDeCuadro[],
  clasificados: ReadonlyMap<number, readonly number[]>,
  bajas: ReadonlySet<number> = new Set(),
): CuadroResuelto {
  const deLugar = (l: Lugar): Lado => {
    if (l.tipo === 'libre') return { libre: true };
    if (l.tipo === 'pareja') return { pareja: l.pareja };
    const id = clasificados.get(l.grupo)?.[l.puesto - 1];
    return id === undefined ? { espera: { tipo: 'grupo', grupo: l.grupo, puesto: l.puesto } } : { pareja: id };
  };

  const buscar = (ronda: number, pos: number) => partidos.find((p) => p.ronda === ronda && p.pos === pos);
  const rondas: CruceResuelto[][] = [];
  const total = cantidadDeRondas(lugares.length);

  for (let ronda = 1; ronda <= total; ronda++) {
    const cantidad = lugares.length / 2 ** ronda;
    const fila: CruceResuelto[] = [];
    for (let pos = 0; pos < cantidad; pos++) {
      const anterior = rondas[ronda - 2];
      const a = ronda === 1 ? deLugar(lugares[pos * 2]!) : anterior![pos * 2]!.avanza;
      const b = ronda === 1 ? deLugar(lugares[pos * 2 + 1]!) : anterior![pos * 2 + 1]!.avanza;
      const guardado = buscar(ronda, pos);
      const id = guardado?.id ?? 0;
      const espera: Lado = { espera: { tipo: 'ganador', ronda, pos } };

      const cruce: CruceResuelto = {
        id, ronda, pos, a, b, marcador: null, avanza: espera,
        pasaDirecto: false, porAbandono: false, sePuedeCargar: false, conflicto: false,
      };

      if (guardado?.marcador && guardado.pareja_a !== null && guardado.pareja_b !== null) {
        // Jugado: mandan los hechos. Se muestra quiénes lo jugaron.
        const jugoA: Lado = { pareja: guardado.pareja_a };
        const jugoB: Lado = { pareja: guardado.pareja_b };
        // Mismas dos parejas, en el lado que sea.
        const mismos =
          esPareja(a) && esPareja(b) &&
          [a.pareja, b.pareja].sort().join() === [guardado.pareja_a, guardado.pareja_b].sort().join();
        cruce.a = jugoA;
        cruce.b = jugoB;
        cruce.marcador = guardado.marcador;
        cruce.avanza = ganador(guardado.marcador) === 'a' ? jugoA : jugoB;
        cruce.conflicto = !mismos;
      } else if (esLibre(a) || esLibre(b)) {
        // Una pasa directo. Si todavía no se sabe quién es, sigue adelante su "de dónde sale".
        cruce.pasaDirecto = true;
        cruce.avanza = esLibre(a) ? b : a;
      } else if (esPareja(a) && esPareja(b)) {
        const aFuera = bajas.has(a.pareja);
        const bFuera = bajas.has(b.pareja);
        if (aFuera !== bFuera) {
          cruce.marcador = { wo: aFuera ? 'b' : 'a', sets: null, tb: null };
          cruce.avanza = aFuera ? b : a;
          cruce.porAbandono = true;
        } else if (!aFuera) {
          cruce.sePuedeCargar = true;
        }
      }
      fila.push(cruce);
    }
    rondas.push(fila);
  }

  const final = rondas[rondas.length - 1]?.[0];
  const campeon = final && final.marcador && esPareja(final.avanza) ? final.avanza.pareja : null;
  return { rondas, campeon };
}
